import { cleanTagsFromText, decodeEntities } from '~/utils/stringHelper';

/**
 * The display name, split so the two halves can sit at opposite corners.
 *
 * Everything after the first word is the second line, so a double-barrelled
 * surname stays together. A single-word name gets the first line alone.
 */
export const splitName = (name: string) => {
    const parts = name.trim().split(/\s+/);
    return { first: parts[0] ?? '', last: parts.slice(1).join(' ') };
};

/** The practice's profile slug, from the link the surface carries. */
export const urlSegmentFromLink = (link?: string) =>
    (link ?? '').split('/').filter(Boolean).pop() ?? '';

/**
 * "a" or "an" for the job title that follows.
 *
 * The first letter is enough for the titles that reach this: Architect,
 * Associate, Engineer and Interior Designer all take "an", and everything else
 * in the list starts on a consonant.
 */
const article = (word: string) => (/^[aeiou]/i.test(word.trim()) ? 'an' : 'a');

/**
 * What the biography column says when the record has no biography.
 *
 * Only what is already known — their role and their practice — and then the
 * two things this overlay can actually do. The booking half is dropped when the
 * gate is off, so the line never points at a button that is not there.
 */
export const fallbackBiography = ({
    displayName,
    isPerson,
    jobTitle,
    practiceName,
    canBook,
}: {
    displayName: string;
    isPerson: boolean;
    jobTitle?: string;
    practiceName: string;
    canBook: boolean;
}) => {
    const ask = canBook ? 'send a message or book a meeting' : 'send a message';
    // Without a name of their own there is no one to put in the sentence, so
    // it asks about the practice instead of reading "X works at X".
    if (!isPerson) {
        return `To find out more about ${practiceName}, please ${ask}.`;
    }
    const role = jobTitle
        ? `works as ${article(jobTitle)} ${jobTitle} at ${practiceName}`
        : `works at ${practiceName}`;
    return `${displayName} ${role}. To find out more, please ${ask}.`;
};

/**
 * The biography's paragraphs.
 *
 * A profile hands its summary over as purified HTML while a project credit
 * hands over plain text, so the block tags become the breaks and the rest is
 * stripped. Without this the profile prints its own `<p>` and `<br />` on the
 * page, because React sets the string as text.
 */
export const toParagraphs = (summary: string) =>
    decodeEntities(
        cleanTagsFromText(
            summary
                .replace(/<\/(p|div|li|h[1-6])>/gi, '\n\n')
                .replace(/<br\s*\/?>/gi, '\n'),
        ),
    )
        .split(/\n+/)
        .map((paragraph) => paragraph.trim())
        .filter(Boolean);
